import React, { memo, useCallback, useState } from 'react'

import {
  AttentionFilled,
  Button,
  Icon,
  TextInput,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { func, string } from 'prop-types'

import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import i18n from 'Pods/Shared/I18nThreeDStudio'

import { NOOP } from 'Utils/stableDefaults'

const ACCESS_CODE_PATH = '/api/internal/generator/access-code'
const INLINE_ERROR_KEYS = {
  invalid_code: 'waitlist.accessCode.invalidMessage',
  already_redeemed: 'waitlist.accessCode.alreadyRedeemedMessage',
}

// Generic access-code entry shared by the 3D Studio usage modal and the AI subscription
// landing waitlist modal. The component owns the POST, the success/error toasts, and the
// inline validation UI; the caller supplies onApplied to persist the (app-specific) result --
// the studio writes it to its store, the landing has nothing to persist. locale is passed in
// (not read from context) so the component stays usable outside the studio page.
function AccessCodeForm({ onApplied = NOOP, onClose: handleClose, locale = 'en' }) {
  const { postData: postAccessCode, isLoading: isApplying } = useApiPost()
  const [code, setCode] = useState('')
  const [codeError, setCodeError] = useState(null)

  const t = useCallback(
    (key, options) => i18n.t(key, { ns: 'three-d-studio', lng: locale, ...options }),
    [locale],
  )

  const handleCodeChange = useCallback((event) => {
    setCode(event.target.value)
    setCodeError(prev => (prev ? null : prev))
  }, [])

  const handleApplyCode = useCallback(() => {
    const trimmed = code.trim()

    if (!trimmed || isApplying) {
      return
    }

    postAccessCode({
      path: ACCESS_CODE_PATH,
      data: { code: trimmed },
      onSuccess: (response) => {
        const data = response?.data?.data || {}

        onApplied(data)
        window.showNotification(t('waitlist.accessCode.successMessage'), { type: 'info', autohide: true })
        handleClose()
      },
      onError: (error) => {
        const inlineErrorKey = INLINE_ERROR_KEYS[error?.response?.data?.error]
        if (inlineErrorKey) {
          setCodeError(t(inlineErrorKey))
        } else {
          window.showNotification(t('waitlist.accessCode.errorMessage'), { type: 'error', autohide: true })
        }
      },
    })
  }, [code, isApplying, postAccessCode, onApplied, handleClose, t])

  return (
    <div className="usage-modal__access-code-form">
      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
      <label
        htmlFor="usage-modal-access-code-input"
        className="usage-modal__access-code-label cgt-body-base-semibold"
      >
        {t('waitlist.accessCode.label')}
      </label>
      <TextInput
        id="usage-modal-access-code-input"
        size="medium"
        placeholder={t('waitlist.accessCode.placeholder')}
        value={code}
        onChange={handleCodeChange}
        isDisabled={isApplying}
        isWarning={!!codeError}
        textInputClassName="usage-modal__access-code-input"
        aria-invalid={codeError ? 'true' : 'false'}
        aria-describedby={codeError ? 'usage-modal-access-code-error' : undefined}
        trailButton={(
          <Button
            size="small"
            type="primary"
            customClass="usage-modal__access-code-apply"
            onClick={handleApplyCode}
            disabled={isApplying || !code.trim()}
          >
            {isApplying ? t('waitlist.accessCode.applying') : t('waitlist.accessCode.apply')}
          </Button>
        )}
        isPilot
      />
      {codeError && (
        <div className="usage-modal__access-code-error-row">
          <Icon
            color="warning"
            notSpaced
            size="small"
            svgComponent={AttentionFilled}
          />
          <p
            id="usage-modal-access-code-error"
            className="usage-modal__access-code-error cgt-body-base"
            role="alert"
          >
            {codeError}
          </p>
        </div>
      )}
    </div>
  )
}

AccessCodeForm.propTypes = {
  onApplied: func,
  onClose: func.isRequired,
  locale: string,
}

export default memo(AccessCodeForm)
